import {
  AuthType,
  decodeO365JwtToken,
  getAccessTokenInfoFromCookie,
  getErrorAsString,
  getMsaImplicitFlowCredentials,
  isMsa,
  isTokenExpired,
  logAndBuildError,
  MsaImplicitAuthScope,
  refreshAccessToken,
  Resource,
} from '@notes/webintegrations-common'
import { IAuthCookie } from '@notes/webintegrations-common/lib/Auth/Cookie/IAuthCookie'
import { createGetUserInfo } from '../../shared/auth/authLibFactories'
import { OAuthContext } from '../../shared/auth/OAuthContext'
import { userInfoCache, userInfoCacheConstantKey } from '../../shared/caches'
import { Log } from '../../shared/logger/Log'
import { IHostIntegration } from '../hostIntegration/IHostIntegration'
import { getAuthTypeWithExtension } from './getAuthTypeWithExtension'
import { getO365AccessTokenInfoFromLocalStorage } from './getO365AccessToken'
import { IAccessTokenInfo } from './IAccessTokenInfo'
import { IAuthResult, IBehaviorOptions, ITokenParameters } from './ISSOTokenInfo'
import { SSOFirstPartyAuthContext } from './SSOFirstPartyAuthContext'

export async function tryGetOAuthTokenInfoWithoutUserAction(): Promise<IAccessTokenInfo | undefined> {
  const oAuthTokenInfo = await tryGetOAuthTokenWithoutUserAction()
  if (!oAuthTokenInfo) {
    Log.logger.error('AuthError: No access token cookie detected. Returning undefined')
    return undefined
  }

  const authType = getAuthTypeWithExtension()
  if (!authType) {
    throw logAndBuildError('AuthError: Unable to get user info getter as the user is unexpectedly unauthenticated!')
  }
  const userInfoGetter = createGetUserInfo(authType)

  try {
    const userInfo = await userInfoGetter.getUserInfo(oAuthTokenInfo.access_token, authType)
    return { userInfo, authContext: new OAuthContext(oAuthTokenInfo.access_token) }
  } catch (error) {
    Log.logger.error(`AuthError: Unexpectedly ran into an error trying to get user info: ${getErrorAsString(error)}. Returning undefined`)
    return undefined
  }
}

export async function tryGetOAuthTokenWithoutUserAction(): Promise<IAuthCookie | undefined> {
  const authType = getAuthTypeWithExtension()

  if (!authType) {
    Log.logger.info('User is not authenticated')
    return undefined
  }

  if (authType === 'o365') {
    let tokenInfo = getAccessTokenInfoFromCookie() ?? getO365AccessTokenInfoFromLocalStorage()
    if (!tokenInfo) {
      Log.logger.info('No access token cookie detected. Returning undefined')
      return undefined
    }

    if (isTokenExpired(tokenInfo.expires_in)) {
      try {
        await refreshAccessToken()
      } catch (error) {
        Log.logger.warn(`Error occurred while refreshing access token: ${getErrorAsString(error)}. Returning undefined`)
        return undefined
      }

      tokenInfo = getAccessTokenInfoFromCookie()
      if (!tokenInfo) {
        Log.logger.warn('No access token cookie detected even after refreshing an existing one. Returning undefined')
        return undefined
      }
    }

    return tokenInfo
  }

  if (authType === 'msa_implicit') {
    return getMsaImplicitFlowCredentials()
  }

  Log.logger.warn(`tryGetOAuthTokenWithoutUserAction got unsupported AuthType: ${authType}`)
  return undefined
}

async function getSSOTokenFirstPartyMsa(host: IHostIntegration, upn: string, authType: AuthType): Promise<IAccessTokenInfo | undefined> {
  Log.logger.info('Getting SSO access tokens for MSA')

  // The first-party SSO API always appends /User.Read to any resource with the graph URL
  // and does not recommend using an explicit scope when making the request
  Log.logger.info(`Getting SSO access token for ${Resource.GraphNotes}`)
  const graphUserToken = await host.getSSOTokenFirstParty(upn, Resource.GraphNotes)

  // Getting substrate token
  Log.logger.info(`Getting SSO access token for ${MsaImplicitAuthScope.NotesInternalReadWrite}`)
  const substrateToken = await host.getSSOTokenFirstParty(upn, MsaImplicitAuthScope.NotesInternalReadWrite)

  const userInfoGetter = createGetUserInfo(authType)
  const msaUser = await userInfoGetter.getUserInfo(graphUserToken, authType)
  return { userInfo: msaUser, authContext: new SSOFirstPartyAuthContext(host, graphUserToken, msaUser, substrateToken) }
}

async function getSSOTokenFirstPartyOrgId(host: IHostIntegration, upn: string): Promise<IAccessTokenInfo | undefined> {
  Log.logger.info('Getting SSO access tokens for OrgId')

  // Get access token for the graph. This will trigger any policies (e.g. 2FA).
  Log.logger.info(`Getting SSO graph access token for ${Resource.GraphNotes}`)
  const graphToken = await host.getSSOTokenFirstParty(upn, Resource.GraphNotes)
  const aadUser = decodeO365JwtToken(graphToken)
  if (!aadUser) {
    Log.logger.info('Unable to get SSO user info')
    return undefined
  }

  // Make sure we cache userInfo. userInfoCache is required to be set since the next time
  // we call getSSOTokenFirstParty, we will try to get a token from an existing userInfo.upn
  // Failing to cache this value will potentially cause us switching an account and resync notes.
  userInfoCache.setInCache(userInfoCacheConstantKey, aadUser)

  // Get access token for OneNote. This is used to exchange for other access tokens.
  Log.logger.info(`Getting SSO OneNote access token for ${Resource.OneNote}`)
  const oneNoteToken = await host.getSSOTokenFirstParty(upn, Resource.OneNote)
  return { userInfo: aadUser, authContext: new SSOFirstPartyAuthContext(host, oneNoteToken, aadUser) }
}

export async function getSSOTokenFirstParty(host: IHostIntegration): Promise<IAccessTokenInfo | undefined> {

  // userInfoCache expires after 30 mins which does not work for SSO scenario as a host session
  // could be longer than 30 mins. We only use upn and authType from userInfoCache
  // hence, we will just ignore the expiry.
  const userInfoCacheWithExpiry = userInfoCache.getWithExpiry(userInfoCacheConstantKey)
  const userInfo = userInfoCacheWithExpiry && userInfoCacheWithExpiry.value
  if (!userInfo || !userInfo.upn || !userInfo.authType) {
    Log.logger.info('No cached user info. Not attempting to get SSO token (first-party)')
    return undefined
  }

  const upn = userInfo.upn
  const authType = userInfo.authType

  Log.logger.info(`Getting SSO token first party by asking the host for our cached userInfo upn with type ${authType}`)
  return getSSOTokenFirstPartyForUser(host, upn, authType)
}

export function getSSOTokenFirstPartyForUser(host: IHostIntegration, upn: string, authType: AuthType): Promise<IAccessTokenInfo | undefined> {
  if (isMsa(authType)) {
    return getSSOTokenFirstPartyMsa(host, upn, authType)
  } else {
    return getSSOTokenFirstPartyOrgId(host, upn)
  }
}

declare var OfficeFirstPartyAuth: {
  getAccessToken(tokenParams: ITokenParameters, behavior: IBehaviorOptions): Promise<IAuthResult | undefined>;
  load(replyUrl?: string): Promise<IAuthResult | undefined>;
  debugging: boolean;
  authFlow: string;
  useMsal5: boolean;
}

/* For ONO SSO, we need to determine the userType for scope and currently only way is to
   call getAuthContextAsync API in OfficeJs which was added for OfficeFirstPartyAuth and not intended
   to be called by add-ins. Adding the type here as we do not have the types defined in Office */
/* tslint:disable */
declare module Office {
  interface Context {
    webAuth: WebAuth
  }
  interface WebAuth {
    authContext: AuthContext
    getAuthContextAsync: (callback: (context: Office.AuthContext) => void) => void
  }
  interface AuthContext {
    value: Value
    status: string
  }
  interface Value {
    authorityType: string
  }
  var context: Context
}
/* tslint:enable */
export function getUserAuthType(): Promise<string> {
  return new Promise<string>((resolve: (value: string) => void, reject: (value: string) => void) => {
    Office.context.webAuth.getAuthContextAsync((c: Office.AuthContext) => {
      if ('succeeded' === c.status) {
        return resolve(c.value.authorityType)
      }
      Log.logger.error('Failed to get the userType for SSO')
      return reject('undefined')
    })
  })
}

export async function getAccessTokenSSO(scope: string): Promise<string | undefined> {
  OfficeFirstPartyAuth.debugging = true
  OfficeFirstPartyAuth.authFlow = 'authcode'
  OfficeFirstPartyAuth.useMsal5 = true
  try {
    await OfficeFirstPartyAuth.load()
  } catch (err) {
    Log.logger.error(`Failed to load first Party Auth with error : ${getErrorAsString(err)}`)
    return undefined
  }
  const tokenParams = {
    resource: scope,
    authChallenge: '',
    policy: '',
  }
  const behaviorParam = {
    popup: false,
    forceRefresh: false,
  }

  const token =  await OfficeFirstPartyAuth.getAccessToken(tokenParams, behaviorParam)
  if (!token) {
    Log.logger.info('Failed to acquire Access Token for SSO')
    return undefined
  }

  return token.accessToken
}

export async function getAccessTokenAADSSO(): Promise<string | undefined> {
  return await getAccessTokenSSO('https://www.onenote.com/')
}

export async function getAccessTokenMSASSO(): Promise<string | undefined> {
  return await getAccessTokenSSO('User.Read')
}

export async function getSSOTokenInfoONO(): Promise<IAccessTokenInfo | undefined> {
  // Default user is assumed to be aad
  let userType = 'aad'
  try {
    userType = await getUserAuthType()
  } catch (error) {
    Log.logger.error(`Error retrieving userAuthType with error: ${getErrorAsString(error)}.`)
  }
  let token = undefined
  let authType: AuthType = 'o365'
  if (userType === 'msa') {
    token = await getAccessTokenMSASSO()
    authType = 'msa_implicit'
  } else {
    token = await getAccessTokenAADSSO()
    authType = 'o365'
  }

  if (!token) {
    return undefined
  }

  const userInfoGetter = createGetUserInfo(authType)
  try {
    const userInfo = await userInfoGetter.getUserInfo(token, authType)
    return { userInfo, authContext: new OAuthContext(token) }
  } catch (error) {
    Log.logger.error(`AuthError: Unexpectedly ran into an error trying to get user info: ${getErrorAsString(error)}. Returning undefined `)
    return undefined
  }
}
